'use client';

import { MinusCircle, PlusCircle } from '@/components/_rebrand/Icons';
import ease from '@/constants/ease';
import cn from 'classnames';
import { motion } from 'framer-motion';
import 'keen-slider/keen-slider.min.css';
import Image from 'next/image';
import { FC, ReactNode, useState } from 'react';
export interface FrontIntelligenceProps {
  firstRowImage?: React.ReactNode;
  firstRowTitle?: string;
  firstRowParagraphs?: ReactNode[];
  secondRowImage: React.ReactNode;
  secondRowTitle: string;
  secondRowTextList: {
    heading: string;
    body: string;
  }[];
  rowsPadding?: 'standard' | 'small';
  aiFeatures?: {
    title: React.ReactNode;
    description: string;
  }[]
  aiFeaturesHeader?: React.ReactNode;
  sideCustomerOne?: React.ReactNode;
  sideCustomerTwo?: React.ReactNode;
}



// TODO: refactor this component to just have rows and not make distinction between firstRow and secondRow
const FrontIntelligence: FC<FrontIntelligenceProps> = ({
  firstRowImage,
  firstRowTitle,
  firstRowParagraphs = [],
  secondRowImage,
  secondRowTitle,
  secondRowTextList = [],
  rowsPadding = 'standard',
  aiFeatures = [],
  aiFeaturesHeader,
  sideCustomerOne,
  sideCustomerTwo,
}
) => {
  const [activeFeatureIndex, setFeatureIndex] = useState(0);

  return (
    <div className="bg-violet-900 text-white px-[16px] md:px-[64px] pb-[48px]">
      <div className="flex">
        <div className="md:pt-[98px] mx-auto max-w-[1384px]">
          <div className="flex flex-wrap border-[1px] border-solid border-violet-800 relative">
            <div className="absolute top-[-17px] left-[-17px] hidden md:block">
              <Image src="https://front.com/webassets/rebrand/ai-plus.svg" width={35} height={35} alt="ai plus" />
            </div>
            {(firstRowTitle || firstRowParagraphs.length > 0 || firstRowImage) && (
              <div className="flex flex-wrap md:flex-nowrap">
                <div className={cn("flex items-center w-full md:pt-14 md:pb-12 md:w-1/2 border-b-[1px] border-solid border-violet-800", rowsPadding === 'small' ? 'px-2 md:px-11 py-2' : 'p-2 md:p-0 md:pr-8 md:pl-[125px]')}>
                  <div className="flex flex-wrap items-start">
                    <h2 className="flex text-h1 mb-4">{firstRowTitle}</h2>
                    <div className="flex flex-col text-p1">
                      {firstRowParagraphs.map((block, index) => (
                        <div key={`ai-features-paragraph-${index}`} className="mb-[24px] last:mb-[34px] flex flex-col">
                          {block}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-center w-full md:w-1/2 px-[16px] md:px-0 md:pt-[94px] md:pb-[94px] md:pl-[114px] md:pr-[125px] border-r-[1px] border-b-[1px] border-solid border-violet-800">
                  {firstRowImage}
                </div>
              </div>
            )}
            <div className="flex flex-wrap md:flex-nowrap bg-center bg-top bg-no-repeat bg-[url('https://front.com/webassets/rebrand/innovation-blur.svg')]">
              <div className="flex items-center justify-center w-full md:w-1/2 border-b-[1px] border-solid border-violet-800">
                {secondRowImage}
              </div>
              <div className={cn("flex w-full md:w-1/2 md:pt-[94px] md:pb-[94px] border-r-[1px] border-b-[1px] border-solid border-violet-800 relative", rowsPadding === 'small' ? 'px-2 md:px-11 py-2' : 'p-2 md:p-0  md:pl-[114px] md:pr-[125px] ')}>
                <div className="absolute top-[-17px] right-[-18px] hidden md:block">
                  <Image src="https://front.com/webassets/rebrand/ai-plus.svg" width={35} height={35} alt="ai plus" />
                </div>
                <div className="flex flex-wrap items-start">
                  <h2 className="flex text-h1">{secondRowTitle}</h2>
                  {secondRowTextList.map((item, index) => (
                    <div key={`ai-features-text-${index}`} className="flex flex-wrap mt-[32px]">
                      {item.heading ? (
                        <div className="flex text-p1 font-500 mb-[12px]">
                          {item.heading}
                        </div>
                      ) : null}
                      <div className="flex text-p2 text-violet-300">
                        {item.body}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap mx-[32px] md:mx-[64px]">
            <div className="flex">
              <div className="flex mt-[64px] md:mt-[120px]">
                <h2 className="text-h1 mb-[54px]">{aiFeaturesHeader}</h2>
              </div>
            </div>
            <div className="flex flex-wrap md:flex-nowrap items-start">
              <div className="flex flex-wrap md:mb-[0] w-full md:w-[70%] md:mr-[140px]">
                {aiFeatures.map((item, index) => (
                  <div key={`ai-features-${index}`} className="flex border-b-[1px] border-solid border-violet-800 w-full items-center justify-between cursor-pointer" onClick={() => { setFeatureIndex(index); }}>
                    <div className="flex text-p1 font-500 mt-[16px] mb-[16px]">
                      <div className="flex flex-wrap">
                        {item.title}
                        {activeFeatureIndex === index && (
                          <motion.div
                            className="text-p2 mt-[10px] font-400 text-violet-300 w-full mr-[8px]"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 400 / 1000,
                              ease: ease.appear,
                            }}
                          >
                            {item.description}
                          </motion.div>
                        )}
                      </div>
                    </div>
                    <div className="flex">
                      {activeFeatureIndex === index ? <motion.div
                        className="text-p2 mt-[10px] font-400 text-violet-300 w-full"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 400 / 1000,
                          ease: ease.appear,
                        }}
                      >
                        {MinusCircle("#DEE948")}
                      </motion.div> : <motion.div
                        className="text-p2 mt-[10px] font-400 text-violet-300 w-full"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          duration: 400 / 1000,
                          ease: ease.appear,
                        }}
                      >
                        {PlusCircle("#FFF")}
                      </motion.div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap w-[30%] hidden md:block">
                {sideCustomerOne}
                {sideCustomerTwo}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontIntelligence;
